
    import Component from "vue-class-component";
    import Vue from "vue";
    import {
        OrganizationLightModel,
        SysAdminOrganizationsClient,
        OrganizationTypeModel,
    } from "@/api-client";
    import { BvTableFieldArray } from "bootstrap-vue";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import HeadingWave from "@/features/shared/components/HeadingWave.vue";
    import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
    import { Prop } from "vue-property-decorator";
    import {isNullOrWhiteSpace} from "@/extensions/string.helpers";
    import {NavigationGuardNext} from "vue-router/types/router";
    import {debounceFn} from "debounce-decorator-ts";

    interface Props {
        preLoadedOrganizations: OrganizationLightModel[];
        orgTypeOptions: OrganizationTypeModel[];
    }

    @Component({
        components: {
            TableEmptySlot,
            HeadingWave,
            SectionPageHeading,
        },
        beforeRouteEnter(to, from, next: NavigationGuardNext<Organizations>) {
            const props = to.meta!.props as Props;
            Promise.all([
                new SysAdminOrganizationsClient().list(),
                new SysAdminOrganizationsClient().organizationTypes()
            ]).then(values => {
                props.preLoadedOrganizations = values[0];
                props.orgTypeOptions = values[1].map(t => ({ name: t.name, value: t.name })) as OrganizationTypeModel[];
                next();
            }).catch(e => next(e))
        }
    })
    export default class Organizations extends Vue implements Props {
        @Prop({ required: true })
        preLoadedOrganizations!: OrganizationLightModel[];
        @Prop({ required: true })
        orgTypeOptions!:OrganizationTypeModel[];

        organizations: OrganizationLightModel[] = this.preLoadedOrganizations;
        filteredOrganizations = this.organizations;

        searchPhrase = "";
        initialSort = { sortBy: "name", sortDesc: false };
        status: any = '';
        statuses = [
            { text: this.$ls.Global_Placeholder_Choose, value: '' },
            { text: this.$ls.Global_Label_Active, value: true },
            { text: this.$ls.Global_Label_InActive, value: false }
        ];
        orgType: any = '';

        created(){
            this.orgTypeOptions.unshift({ name: this.$ls.Global_Placeholder_Choose, value: '' } as OrganizationTypeModel);
            this.getParametersFromRouteOrDefault();

            this.$watch(() => this.searchPhrase, () => {
                this.reflectParametersToRoute();
            });
            this.$watch(() => this.orgType, () => {
                this.reflectParametersToRoute();
            });
            this.$watch(() => this.status, () => {
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.$route.query, () => {
                this.search();
            }, { deep: true, immediate: true });
        }

        navigateToDetails(organization: OrganizationLightModel) {
            this.$router.push({
                name: "admin-organization",
                params: { id: organization.id },
            });
        }

        search(){
            this.filteredOrganizations = this.organizations.filter(o => (o.name.toLowerCase().indexOf(this.searchPhrase.toLowerCase()) >= 0 || isNullOrWhiteSpace(this.searchPhrase)) && ( typeof this.status != "boolean" || o.active === this.status) && (isNullOrWhiteSpace(this.orgType) || o.types.includes(this.orgType)));
        }

        @debounceFn(100) // Prevents possible double-update
        reflectParametersToRoute(): void {
            this.$router.replace({
                query: {
                    // Setting to 'undefined' removes parameter from URL.
                    searchPhrase: this.searchPhrase || undefined,
                    status: this.status != null ? this.status.toString() : undefined,
                    type: this.orgType || undefined
                }
            });
        }

        getParametersFromRouteOrDefault() {
            const route = this.$route as any;
            const query = route.query;
            this.searchPhrase = query.searchPhrase ? query.searchPhrase : '';
            this.status = query.status ? query.status : '';
            this.orgType = query.type ? query.type : '';
        }

        fields: BvTableFieldArray = [
            {
                key: "name",
                label: this.$ls.Global_Label_Name,
                sortable: true,
                thClass: "w-50"
            },
            {
                key: "types",
                label: this.$ls.Administration_Organization_Table_Label_Type,
                sortable: true,
                formatter: (
                    dateString: string,
                    key: string,
                    item: OrganizationLightModel
                ) => item.types.join(", ") || "-",
            },
            {
                key: "active",
                label: this.$ls.Administration_Organization_Table_Label_Status,
                sortable: true,
                formatter: (item: boolean): string => (item ? this.$ls.Global_Label_Active: this.$ls.Global_Label_InActive)
            },
            {
                key: "linkIcon",
                label: "",
                sortable: false,
                tdClass:"table-actions"
            },
        ];
    }
